import React, { useCallback, useState } from "react";
import { Grid } from "@mui/material";
import csvDownload from "json-to-csv-export";

import { defined } from "@formant/core/common/defined";
import { IUsageMetric } from "@formant/core/model/IUsageMetric";
import { MetricType } from "@formant/core/model/MetricType";

import { usageMetricsClient } from "../../../clients";
import { authStore } from "../../../stores";
import { showMessenger } from "../../../utils/intercom";
import { StandardButton } from "../../common/StandardButton";
import * as styles from "./index.scss";

export interface IUsageButtonGroupProps {
    start: Date;
    end: Date;
    organizationId: string | undefined;
    isUsageDataAvailable: boolean;
}

interface IReportRow {
    "Organization Id": string;
    Time: string;
    "Usage Type": string;
    "Usage Value": string;
    "Device Id": string;
    "User Id": string;
}

function downloadDataFormat(metrics: IUsageMetric): IReportRow {
    return {
        "Organization Id": metrics.organizationId,
        Time: metrics.time,
        "Usage Type": metrics.metricType,
        "Usage Value": `${metrics.metricValue}`,
        "Device Id": metrics.deviceId ?? "",
        "User Id": metrics.userId ?? "",
    };
}

const emptyReport: IReportRow[] = [
    {
        "Organization Id": "",
        Time: "",
        "Usage Type": "",
        "Usage Value": "",
        "Device Id": "",
        "User Id": "",
    },
];

export function UsageButtonGroup({
    start,
    end,
    organizationId,
    isUsageDataAvailable,
}: IUsageButtonGroupProps) {
    const [isLoadingCSV, setIsLoadingCSV] = useState(false);
    const generateCSV = useCallback(async () => {
        setIsLoadingCSV(true);
        const token = await authStore.token();

        const acceptedMetricTypes: MetricType[] = [
            "data-in-count",
            "data-in-bytes",
            "data-out-bytes",
            "data-stored-bytes",
        ];

        const CSV = await Promise.all(
            acceptedMetricTypes.map(metricType => {
                return usageMetricsClient.query(
                    token,
                    defined(organizationId),
                    {
                        start: start.toISOString(),
                        end: end.toISOString(),
                        detailed: true,
                        metricType,
                    },
                );
            }),
        );

        const flatCSV = CSV.flatMap(obj => obj.items);
        setIsLoadingCSV(false);
        const downloadData =
            flatCSV.length > 0
                ? flatCSV.map(val => {
                      return downloadDataFormat(val);
                  })
                : emptyReport;
        csvDownload({
            data: downloadData,
            filename: "usage_report",
            delimiter: ",",
        });
    }, [start, end]);
    return (
        <Grid container={true} columnSpacing={2} rowSpacing={2}>
            <Grid
                item={true}
                container={true}
                xs={12}
                justifyContent="center"
                alignItems="center"
                alignSelf="center"
            >
                <StandardButton
                    disabled={!isUsageDataAvailable}
                    dark={true}
                    loading={isLoadingCSV}
                    className={styles["button-group-table"]}
                    onClick={generateCSV}
                >
                    Download usage data
                </StandardButton>
            </Grid>

            <Grid
                item={true}
                container={true}
                xs={12}
                justifyContent="center"
                alignItems="center"
                alignSelf="center"
            >
                <StandardButton
                    dark={true}
                    className={styles["button-group-table"]}
                    onClick={showMessenger}
                >
                    Discuss with customer support...
                </StandardButton>
            </Grid>
        </Grid>
    );
}
